<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from "vue";
import { useConfirm } from "../composables/useConfirm.js";
import { useI18n } from "../composables/useI18n.js";

/**
 * The single confirmation dialog for the whole app, mounted once in App.vue.
 * Everything that used to call `window.confirm` goes through `useConfirm()`.
 */

const { t } = useI18n();
const { request, accept, cancel } = useConfirm();

const confirmLabel = computed(() => request.value?.confirmLabel ?? t("Confirm"));
const cancelLabel = computed(() => request.value?.cancelLabel ?? t("Cancel"));

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") cancel();
}

watch(request, (value) => {
  if (typeof window === "undefined") return;
  if (value) window.addEventListener("keydown", onKeydown);
  else window.removeEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="request"
      class="confirm-scrim"
      role="dialog"
      aria-modal="true"
      @click.self="cancel"
    >
      <div class="confirm-card">
        <h2 v-if="request.title" class="confirm-title">{{ request.title }}</h2>
        <p class="confirm-message">{{ request.message }}</p>
        <div class="confirm-actions">
          <button type="button" class="confirm-cancel" @click="cancel">{{ cancelLabel }}</button>
          <button
            type="button"
            class="confirm-accept"
            :class="{ 'is-danger': request.danger !== false }"
            autofocus
            @click="accept"
          >{{ confirmLabel }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.confirm-scrim {
  position: fixed;
  inset: 0;
  z-index: 300; /* above the drag ghost, which is the highest thing otherwise */
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(4, 8, 6, 0.72);
  backdrop-filter: blur(2px);
}

.confirm-card {
  width: min(420px, 100%);
  box-sizing: border-box;
  padding: 22px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  background: var(--card-gradient);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.55);
}

.confirm-title {
  margin: 0 0 10px;
  font-family: var(--font-display);
  font-size: 1.3rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--text-strong);
}

.confirm-message {
  margin: 0 0 20px;
  line-height: 1.5;
  color: var(--text-body);
}

.confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.confirm-actions button {
  font: inherit;
  font-weight: 700;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.confirm-cancel {
  background: var(--control-bg);
  border: 1px solid var(--control-border);
  color: var(--text);
}

.confirm-cancel:hover { background: var(--control-bg-hover); }

.confirm-accept {
  border: 1px solid var(--accent-strong);
  background: var(--cta-gradient);
  color: var(--text-on-accent);
}

.confirm-accept:hover { background: var(--cta-gradient-hover); }

.confirm-accept.is-danger {
  border-color: var(--danger-border);
  background: var(--danger-bg);
  color: var(--danger-text);
}

.confirm-accept.is-danger:hover { background: rgba(142, 52, 56, 0.34); }

@media (max-width: 480px) {
  .confirm-actions { flex-direction: column-reverse; }
  .confirm-actions button { width: 100%; }
}
</style>
